import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch,
  Space, Popconfirm, message,
} from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiErrorMessage } from '../../utils/apiError';
import {
  getCourses, createCourse, updateCourse, deleteCourse,
} from '../../api/courses';

const BranchCoursesTab = ({ branchId }) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  const fetchCourses = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getCourses({ page, per_page: 15, branch_id: branchId });
      setCourses(res.data.data);
      setPagination({
        current: res.data.current_page,
        pageSize: res.data.per_page,
        total: res.data.total,
      });
    } catch {
      message.error('Sertifika Programları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (branchId) fetchCourses();
  }, [branchId]);

  const handleSubmit = async (values) => {
    try {
      const data = { ...values, branch_id: branchId };
      if (editingCourse) {
        await updateCourse(editingCourse.id, data);
        message.success('Sertifika Programı güncellendi.');
      } else {
        await createCourse(data);
        message.success('Sertifika Programı oluşturuldu.');
      }
      setModalOpen(false);
      form.resetFields();
      setEditingCourse(null);
      fetchCourses();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    form.setFieldsValue(course);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCourse(id);
      message.success('Sertifika Programı silindi.');
      fetchCourses();
    } catch {
      message.error('Sertifika Programı silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Sertifika Programı Adı',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (v) => v || '—',
    },
    {
      title: 'Şablon',
      key: 'template',
      render: (_, record) => record.certificate_template
        ? <CheckCircleFilled style={{ color: '#52c41a' }} />
        : <CloseCircleFilled style={{ color: '#ff4d4f' }} />,
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active, record) => (
        <Switch
          checked={is_active}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          style={{ backgroundColor: is_active ? '#52c41a' : '#ff4d4f' }}
          onChange={async (checked) => {
            try {
              await updateCourse(record.id, { is_active: checked });
              fetchCourses();
            } catch {
              message.error('Durum güncellenemedi.');
            }
          }}
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/courses/${record.id}`, { state: { fromBranchId: branchId } })}
          >
            Projeler
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Düzenle
          </Button>
          <Popconfirm
            title="Sertifika Programını silmek istediğinize emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button icon={<DeleteOutlined />} danger size="small">Sil</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingCourse(null);
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Yeni Sertifika Programı
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={courses}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (page) => fetchCourses(page),
          showTotal: (total) => `Toplam ${total} program`,
        }}
      />

      <Modal
        title={editingCourse ? 'Sertifika Programı Düzenle' : 'Yeni Sertifika Programı'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); setEditingCourse(null); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Program Adı" rules={[{ required: true }]}>
            <Input placeholder="Robotik Programlama" />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={3} placeholder="Program açıklaması" />
          </Form.Item>
          {editingCourse && (
            <Form.Item name="is_active" label="Durum" valuePropName="checked">
              <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
            </Form.Item>
          )}
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); setEditingCourse(null); }}>İptal</Button>
              <Button type="primary" htmlType="submit">
                {editingCourse ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BranchCoursesTab;
